import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy, UrlSegment
} from '@angular/router';
import { Component, Injectable } from "@angular/core";
import { LMContextService, LMRouteComponentService, LMSpecService, LMUrlUtilService } from './services';
import { LMBaseComponent, LMFormComponent, LMListComponent, LMPageComponent } from '@app/components';
import { environment } from '@environments/environment';
import { AppConstants } from '@app/utils';

@Injectable()
export class LMRouteReuseStrategy extends RouteReuseStrategy {
  /**
   * Determines if this route (and its subtree) should be detached to be reused later
   */

  private max = 10;
  private DELIMITER: string = '-';
  private routeCache = new Map<string, DetachedData>();
  private futureComp: Component;
  private enteredIndex = 0;

  constructor(
    private currentComponent: LMRouteComponentService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmSpecService: LMSpecService,
    private lmContextService: LMContextService
  ) {
    super();
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (!this.lmContextService.isRouteReuse())
      return false;
    return this.isReusableContent(route);
  }

  /**
   * Stores the detached route
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
  //  const currentComponent: Component = this.currentComponent.getRouteComponent();
    let currentComponent: Component = this.getCompFromRoute(route);
    this.setRouteReuseActive(currentComponent,false, '');
    let key: string = this.getKeyFromRoute(route);

    // if (!environment.production) {
    //   console.log('**ROUTEREUSE** storing route key ' + this.getKeyFromRoute(route));
    // }
    this.addRouteItem(key, handle, route);
    // if (!environment.production) {
    //   console.log("**ROUTEREUSE** Rough Memory used for route reuse cashe in " + this.roughSizeOfObject(this.routeCache));
    // }
  }

  getSpec(): LMListSpec | LMFormSpec | LMPageSpec {
    const currentComponent: Component = this.currentComponent.getRouteComponent();
    return (currentComponent as LMBaseComponent).getSpec();
  }

  // **************************************************
  // there is no guarentee that this method id called only once on a perticular route reload.
  // https://github.com/angular/angular/issues/43251
  // part of a response to a bug written and closed on this issue is below
  // There are no guarantees from a public API standpoint as to how many times the router will call these methods.
  // so code must be dded to only run the code once after a route has been reused to avoid situation like data reloading ore than once.
  /**
   * Determines if this route (and its subtree) should be reattached
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!this.lmContextService.isRouteReuse())
      return false;
    // Should this route be attached
    return (this.isReusableContent(route) && this.hasHandle(route));
  }

  /**
   * Retrieves the previously stored route
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    // const currentComponent: Component = this.currentComponent.getRouteComponent();
    let comp: Component = this.getCompFromRoute(route);
    if (this.isReusableContent(route)) {
      if (this.hasHandle(route)) {
        // Fire the event tolet the app know that params might have changed
        setTimeout(() => {
          try {

            const recordResource: string = this.getRecordResource(route);
            const recordKey: string = this.getRecordKeysFromSegment(recordResource);

            this.setRouteReuseActive(comp, true, recordKey);
          } catch(e) {
            alert("error")
          }

        }, 1);
        // if (!environment.production) {
        //   console.log('**ROUTEREUSE** Loading route key ' + this.getKeyFromRoute(route));
        // }
        return this.getHandle(route);
      }
    }
    this.setRouteReuseActive(comp, true, '');
    return null;
  }

  /**
   * Determines if a route should be reused
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // should the route coming in be reused from stored
    if (this.isReusableContent(future) && this.hasHandle(future)) {
      const comp = this.getCompFromRoute(future);
      this.futureComp = comp;
      return true;
    }
    return false;
  }

  private getResolvedUrl(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
    .map(v => v.url.map(segment => segment.toString()).join('/'))
    .join('/');
  }
  private getConfiguredUrl(route: ActivatedRouteSnapshot): string {
    return '/' + route.pathFromRoot
    .filter(v => v.routeConfig)
    .map(v => v.routeConfig!.path)
    .join('/');
  }

  private setRouteReuseActive(comp: Component, active: boolean, recordKey: string) {
    if (comp instanceof LMFormComponent) {
      (comp as LMFormComponent).setRouteReuseActive(active, recordKey);
    } else if (comp instanceof LMListComponent) {
      return (comp as LMListComponent).setRouteReuseActive(active);
    } else if (comp instanceof LMPageComponent) {
      return (comp as LMPageComponent).setRouteReuseActive(active);
    }
    return undefined;
  }

  private isReusableContent(route: ActivatedRouteSnapshot): boolean {
    const key = this.getKeyFromRoute(route);
    return (key.indexOf(this.DELIMITER + 'form' + this.DELIMITER) >= 0);  // || (key.indexOf(this.DELIMITER + 'list-' + this.DELIMITER) >= 0) );

//    return ((key.indexOf(this.DELIMITER + '-html' + this.DELIMITER) >= 0) || (key.indexOf(this.DELIMITER + 'form' + this.DELIMITER) >= 0) || (key.indexOf(this.DELIMITER + 'list' + this.DELIMITER) >= 0) || (key.indexOf(this.DELIMITER + 'page' + this.DELIMITER) >= 0));
  }

  private getKeyFromRoute(route: ActivatedRouteSnapshot): string {
    // this need to be rewritten. done for a quick test.
    let key = '';
    if (route.url.length > 0) {
      const segments: UrlSegment[] = route.url;
      key = this.getKeyFromUrlSegments(segments);
    }
    const action: string = route.queryParams.action;

    return key + (action && action.length > 0 ? this.DELIMITER + action : '');
  }

  private getKeyFromUrlSegments(segments: UrlSegment[]): string {
      let routeString = '';
      segments.forEach((segment, index) => {
        if (segment?.path.indexOf(":") >= 0) {
          return routeString
        }
        if(index ===3 && segment?.path?.indexOf("(") >= 0) {
          routeString += segment.path.replace(/\([^)]*\)/g, '()') + this.DELIMITER;
        }
        else {
          routeString += segment.path + this.DELIMITER;
        }
      });
      // Remove the trailing slash
      routeString = routeString.slice(0, -1);
      return routeString;
  }

  private getRecordKeysFromSegment(inputString: string): string {
    const matches = inputString.match(/\((.*?)\)/g); // Use non-greedy match to capture content between parentheses
    if (matches) {
      return matches.map(match => match.slice(1, -1))[0]; // Remove parentheses from matches
    }
    return '';
  }

  private getRecordResource(route: ActivatedRouteSnapshot): string {
    const segments: UrlSegment[] = route.url;
    return segments[3].path;
  }

  private getCompFromRoute(route: ActivatedRouteSnapshot): Component {
    let retComp: Component;
    const key: string = this.getKeyFromRoute(route);
    const routeCacheData: DetachedData  = this.routeCache.get(key);
    try {
      retComp =  routeCacheData.component;
    } catch (e) {
      retComp = this.currentComponent.getRouteComponent() as Component;
    }
    return retComp;
  }

  private hasHandle(route: ActivatedRouteSnapshot): boolean {
    let handle: DetachedRouteHandle = this.getHandle(route);
    return handle !== undefined;
  }

  private getHandle(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    const key: string = this.getKeyFromRoute(route);
    const handle: DetachedRouteHandle = this.routeCache.get(key)?.handle;
    return handle;
  }

  private getParams(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    const key: string = this.getKeyFromRoute(route);
    const params: DetachedRouteHandle = this.routeCache.get(key)?.handle;
    return params;
  }

  private isRouteForm(route: ActivatedRouteSnapshot): boolean {
    const key: string = this.getKeyFromRoute(route);
    return key.indexOf(this.DELIMITER + 'form' + this.DELIMITER) > 0;
  }

  private isRouteList(route: ActivatedRouteSnapshot): boolean {
    const key: string = this.getKeyFromRoute(route);
    return key.indexOf(this.DELIMITER + 'list' + this.DELIMITER) > 0;
  }

  private isRoutePage(route: ActivatedRouteSnapshot): boolean {
    const key: string = this.getKeyFromRoute(route);
    return key.indexOf(this.DELIMITER + 'page' + this.DELIMITER) > 0;
  }

  public addRouteItem(key: string, handle: DetachedRouteHandle, route: ActivatedRouteSnapshot) {
    const verifyCount: DetachedData = this.routeCache.get(key);
    const count = verifyCount ? verifyCount.count+1 : 1;

    if (this.routeCache.size >= this.max) {
      if (!environment.production) {
        console.log('**ROUTEREUSE** Adding route eceeds MAX and 1 currntly cached will be dropped');
      }
      const removeList = Array.from(this.routeCache);
      removeList.sort((a, b) => ((b[1].timestamp as number) - (a[1].timestamp as number)));
      //removeList.sort((a, b) => 4);
      for (let x = 0; x < removeList.length; x++) {
        removeList[ x ][ 1 ].points = (x + 1) + (removeList[ x ][ 1 ].count * 2);
      }
      removeList.sort((a, b) => ((a[ 1 ].points as number) - (b[ 1 ].points as number)));

      this.routeCache = this.arrayToMap(removeList.slice(1));
    }
    const d = new Date();
    let currentComponent: Component;

    try {
      currentComponent = this.getCompFromRoute(route);
    } catch (e) {
      currentComponent = this.currentComponent.getRouteComponent();
    }

    this.routeCache.set(key, { timestamp: d.getTime(), handle: handle, component: currentComponent, params: route.params, count: count, enteredIndex: this.enteredIndex++, points: 0 });
  }

  private  arrayToMap(array: [ key: string, detachedData: DetachedData ][]): Map<string, DetachedData> {
    const map = new Map<string, DetachedData>();

    // Loop through the array and add key-value pairs to the map
    array.forEach(item => {
      map.set(item[0], item[1]);
    });

    return map;
  }

  private roughSizeOfObject(obj) {
    let bytes = 0;

    obj.forEach((value) => {
      bytes += value.component.elementRef.nativeElement.outerHTML.length
    });

    return bytes;
  }
}

export interface DetachedData {
  timestamp: Number,
  handle: DetachedRouteHandle,
  component: Component,
  params: Params,
  count: number,
  enteredIndex: number,
  points: number
}

// export interface RouteReuseStatus {
//   routeReuseActive : boolean;
//   isRouteReuseActive(): boolean;
//   setRouteReuseActive(active: boolean): void;
//   handleRouteReuseActivated(): void
//   getSpec(): LMListSpec | LMFormSpec | LMPageSpec;
// }
